import { CommonModule } from '@angular/common';
import { Component, inject, DestroyRef, OnInit } from '@angular/core';
import { BackButtonComponent } from '@app/shared/components/back-button/back-button.component';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, Observable, of, forkJoin } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import {
  BolsaInstituicaoCursoService,
  BolsaService,
  InstituicaoEnsinoService,
  InstituicaoEnsinoCursoService,
  FaseCursoService,
} from '../../api/services';
import { ApiService } from '@core/providers/http';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { TiposBolsaPipe } from '@app/features-modules/gestao-bolseiros/pipes/tipos-bolsa.pipe';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { Pagination } from '@shared/model/pagination.model';
import { TableComponent } from '@app/shared/components/table/table.component';
import { MenuItem, ConfirmationService } from 'primeng/api';
import { TableColumn } from '@shared/components/table/table.component';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
  ValidationErrors,
} from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DialogModule } from 'primeng/dialog';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { DropdownModule } from 'primeng/dropdown';
import { DatePickerModule } from 'primeng/datepicker';
import { SearchBarComponent } from '@app/shared/components/search-bar/search-bar.component';
import { DataVoidComponent } from '@app/shared/components/data-void/data-void.component';
import { TabsModule } from 'primeng/tabs';
import { RenovacaoComponent } from './renovacao/renovacao.component';
import { InputTextModule } from 'primeng/inputtext';
import { Select } from 'primeng/select';

@Component({
  selector: 'app-detalhes-auxilio-formacao',
  imports: [
    CommonModule,
    BackButtonComponent,
    TableModule,
    ButtonModule,
    TagModule,
    TiposBolsaPipe,
    TableComponent,
    InputTextModule,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
    ReactiveFormsModule,
    DropdownModule,
    DatePickerModule,
    SearchBarComponent,
    DataVoidComponent,
    TabsModule,
    RenovacaoComponent,
    Select,
  ],
  providers: [
    ApiService,
    BolsaService,
    BolsaInstituicaoCursoService,
    InstituicaoEnsinoService,
    InstituicaoEnsinoCursoService,
    FaseCursoService,
    MessageService,
    ConfirmationService,
  ],
  templateUrl: './detalhes-auxilio-formacao.component.html',
  styleUrl: './detalhes-auxilio-formacao.component.scss',
})
export class DetalhesAuxilioFormacaoComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  apiService = inject(ApiService);
  bolsaService = inject(BolsaService);
  bolsaInstituicaoCursoService = inject(BolsaInstituicaoCursoService);
  instituicaoEnsinoService = inject(InstituicaoEnsinoService);
  instituicaoEnsinoCursoService = inject(InstituicaoEnsinoCursoService);
  faseCursoService = inject(FaseCursoService);
  messageService = inject(MessageService);
  confirmationService = inject(ConfirmationService);
  fb = inject(FormBuilder);

  // Listas para dropdowns
  listaUnidadesOrganicas: any[] = [];
  educationalInstitution: any[] = [];
  instituicaoCursoList: any[] = [];
  listaFases: any[] = [];

  // Tabela
  columns: TableColumn[] = [
    {
      field: 'instituicaoGestoraNome',
      header: 'Instituição Gestora',
      type: 'text',
      tooltip: 'Instituição responsável pela bolsa',
    },
    {
      field: 'cursoDesignacao',
      header: 'Curso',
      type: 'text',
      tooltip: 'Curso vinculado à bolsa',
    },
    {
      field: 'instituicaoEnsinoNome',
      header: 'Instituição de Ensino',
      type: 'text',
      tooltip: 'Nome da instituição de ensino',
    },
    {
      field: 'aproveitamento',
      header: 'Aproveitamento',
      type: 'tag',
      severityField: 'aproveitamento',
      tagSeverityFunction: (rowData: any) =>
        this.getSeverity(rowData.aproveitamento),
      tooltip: 'Status do aproveitamento',
    },
  ];
  actions: MenuItem[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      command: ($event: any) =>
        this.editBolsaInstituicaoCurso($event.item.data),
    },
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      command: ($event: any) =>
        this.confirmDeleteBolsaInstituicaoCurso($event.item.data),
    },
  ];

  // Dados
  bolsa: any;
  isLoading: boolean = true;
  pagination: Pagination<any> = new Pagination<any>();

  // Modal
  visible: boolean = false;
  currentEditIndex: number | null = null;
  institutionForm: FormGroup;

  constructor() {
    this.institutionForm = this.fb.group(
      {
        id: [''],
        instituicaoEnsinoID: [null, Validators.required],
        faseCursoID: [null, Validators.required],
        unidadeOrganicaID: [''],
        instituicaoEnsinoCursoID: [null, Validators.required],
        dataInicio: [null, Validators.required],
        dataFim: [null, Validators.required],
        duracao: [{ value: '', disabled: true }],
        // Campos internos para payload
        aproveitamento: [null],
        anoCurricular: [null],
        c: [null], // campo de concorrência (se existir)
      },
      { validators: this.dateRangeValidator },
    );

    // Quando instituição ou fase mudarem, recarregar cursos e unidades
    this.institutionForm
      .get('instituicaoEnsinoID')
      ?.valueChanges.subscribe(() => {
        this.updateDependentLists();
      });
    this.institutionForm.get('faseCursoID')?.valueChanges.subscribe(() => {
      this.updateDependentLists();
    });

    // Listener para cálculo de duração
    this.setupDurationListener();
  }

  ngOnInit(): void {
    forkJoin({
      bolsasCurso: this.loadAllBolsaInstituicaoCurso(),
      instituicoes: this.getAllInstituicoesEnsino(),
      fases: this.getAllFaseCurso(),
    }).subscribe(() => {
      this.loadBolsaDetails();
    });
  }

  // ------------------------------------------------------------
  // CÁLCULO DE DURAÇÃO
  // ------------------------------------------------------------
  private setupDurationListener(): void {
    const inicioCtrl = this.institutionForm.get('dataInicio');
    const fimCtrl = this.institutionForm.get('dataFim');
    if (!inicioCtrl || !fimCtrl) return;

    combineLatest([inicioCtrl.valueChanges, fimCtrl.valueChanges])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([inicio, fim]) => {
        this.calculateDuration(inicio, fim);
      });
  }

  private calculateDuration(inicio: Date | null, fim: Date | null): void {
    if (!inicio || !fim) {
      this.institutionForm.patchValue({ duracao: '' }, { emitEvent: false });
      return;
    }
    const start = new Date(inicio);
    const end = new Date(fim);
    if (end < start) {
      this.institutionForm.patchValue({ duracao: '' }, { emitEvent: false });
      return;
    }

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
      months--;
      const lastDayPrevMonth = new Date(
        end.getFullYear(),
        end.getMonth(),
        0,
      ).getDate();
      days += lastDayPrevMonth;
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    const parts: string[] = [];
    if (years > 0) parts.push(`${years} ano${years > 1 ? 's' : ''}`);
    if (months > 0) parts.push(`${months} mês${months > 1 ? 'es' : ''}`);
    if (days > 0) parts.push(`${days} dia${days > 1 ? 's' : ''}`);

    let duration = parts.length ? parts.join(' e ') : '0 dias';
    this.institutionForm.patchValue(
      { duracao: duration },
      { emitEvent: false },
    );
  }

  // ------------------------------------------------------------
  // VALIDADOR DE DATAS
  // ------------------------------------------------------------
  dateRangeValidator(group: FormGroup): ValidationErrors | null {
    const inicio = group.get('dataInicio')?.value;
    const fim = group.get('dataFim')?.value;
    if (inicio && fim && new Date(fim) < new Date(inicio)) {
      return { dateRange: true };
    }
    return null;
  }

  // ------------------------------------------------------------
  // FORMATAÇÃO DE DATAS
  // ------------------------------------------------------------
  private formatDateToISO(date: Date | string | null): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toISOString();
  }

  private parseDateFromBackend(dateStr: string): Date | null {
    if (!dateStr) return null;
    const parts = dateStr.split('/');
    if (parts.length !== 3) return new Date(dateStr);
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const year = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  // ------------------------------------------------------------
  // CARREGAR LISTAS
  // ------------------------------------------------------------
  getAllInstituicoesEnsino(): Observable<any> {
    return this.instituicaoEnsinoService.list({ pageSize: 100 }).pipe(
      tap((institutions) => (this.educationalInstitution = institutions.data)),
      catchError((err) => {
        console.error('Erro ao carregar instituições:', err);
        return of(null);
      }),
    );
  }

  getAllFaseCurso(): Observable<any> {
    return this.faseCursoService.list().pipe(
      tap((response) => (this.listaFases = response)),
      catchError((err) => {
        console.error('Erro ao carregar fases:', err);
        return of(null);
      }),
    );
  }

  getAllInstituicaoCurso(params: any, callback?: (data: any[]) => void): void {
    this.instituicaoEnsinoCursoService.list(params).subscribe(
      (response) => {
        this.instituicaoCursoList = response.data.map((item: any) => ({
          ...item,
          id: item?.id,
          nome: item?.curso,
        }));
        this.isLoading = false;
        if (callback) {
          callback(this.instituicaoCursoList);
        }
      },
      (error) => console.error('Erro ao buscar cursos:', error),
    );
  }

  loadAllUnidadeOrganica(instituicaoEnsinoID: number): void {
    if (!instituicaoEnsinoID) {
      this.listaUnidadesOrganicas = [];
      return;
    }
    this.instituicaoEnsinoService
      .list({ InstituicaoPaiID: instituicaoEnsinoID, pageSize: 100 })
      .subscribe((res) => {
        this.listaUnidadesOrganicas = res.data.map((item: any) => ({
          ...item,
          id: item?.id,
          designacao: item?.nome,
        }));
      });
  }

  private updateDependentLists(): void {
    const ensinoID = this.institutionForm.get('instituicaoEnsinoID')?.value;
    const faseID = this.institutionForm.get('faseCursoID')?.value;
    if (ensinoID && faseID) {
      this.getAllInstituicaoCurso({
        InstituicaoEnsinoID: ensinoID,
        CategoriaEnsinoCursoID: faseID,
        pageSize: 100,
      });
      this.loadAllUnidadeOrganica(ensinoID);
    } else {
      this.instituicaoCursoList = [];
      this.listaUnidadesOrganicas = [];
    }
  }

  // ------------------------------------------------------------
  // OPERAÇÕES CRUD
  // ------------------------------------------------------------
  loadAllBolsaInstituicaoCurso(queryParams: any = {}): Observable<any> {
    const params = {
      ...queryParams,
      pageNumber: this.pagination.pageNumber ?? 1,
      pageSize: this.pagination.pageSize ?? 5,
      BolsaID: this.bolsaId,
      OrderBy: 'id desc',
    };
    return this.bolsaInstituicaoCursoService.list(params).pipe(
      tap((response) => {
        this.pagination = new Pagination<any>().deserialize(response);
      }),
      catchError((err) => {
        console.error('Erro ao carregar lista:', err);
        return of(null);
      }),
    );
  }

  loadBolsaDetails(): void {
    this.bolsaService.show(this.bolsaId).subscribe(
      (bolsa: any) => {
        this.bolsa = bolsa.data;
        this.handleFaseCursoByTipoBolsa(this.bolsa.tipoBolsa);
      },
      (error: any) => console.error('Erro ao carregar bolsa:', error),
    );
  }

  handleFaseCursoByTipoBolsa(tipoBolsa: any): void {
    const faseControl = this.institutionForm.get('faseCursoID');
    if (tipoBolsa == 1) {
      const fase = this.listaFases.find(
        (f) => f.designacao === 'Ensino Superior',
      );
      if (fase) {
        faseControl?.enable();
        faseControl?.setValue(fase.id);
        faseControl?.disable();
      }
    } else {
      faseControl?.enable();
      faseControl?.setValue(null);
    }
  }

  // ------------------------------------------------------------
  // MODAL - ABRIR PARA CRIAÇÃO
  // ------------------------------------------------------------
  showAddFormacaoModal(): void {
    this.currentEditIndex = null;
    this.institutionForm.reset({
      id: '',
      instituicaoEnsinoID: null,
      faseCursoID: null,
      unidadeOrganicaID: null,
      instituicaoEnsinoCursoID: null,
      dataInicio: null,
      dataFim: null,
      duracao: '',
      aproveitamento: null,
      anoCurricular: null,
      c: null,
    });
    if (this.bolsa) {
      this.handleFaseCursoByTipoBolsa(this.bolsa.tipoBolsa);
    }
    this.visible = true;
  }

  // ------------------------------------------------------------
  // MODAL - ABRIR PARA EDIÇÃO (CORRIGIDO)
  // ------------------------------------------------------------
  editBolsaInstituicaoCurso(bolsaInstituicaoCurso: any): void {
    this.currentEditIndex = bolsaInstituicaoCurso.id;

    // Preenche os campos básicos
    const formValue: any = {
      id: bolsaInstituicaoCurso.id,
      instituicaoEnsinoID: bolsaInstituicaoCurso.instituicaoEnsinoID || null,
      faseCursoID: bolsaInstituicaoCurso.faseId || null,
      unidadeOrganicaID: bolsaInstituicaoCurso.unidadeOrganicaID || null,
      dataInicio: this.parseDateFromBackend(bolsaInstituicaoCurso.dataInicio),
      dataFim: this.parseDateFromBackend(bolsaInstituicaoCurso.dataFim),
      aproveitamento: bolsaInstituicaoCurso.aproveitamentoID ?? 1,
      anoCurricular: bolsaInstituicaoCurso.anoCurricular ?? 1,
      c: bolsaInstituicaoCurso.c || null,
      // Não setamos instituicaoEnsinoCursoID ainda, será definido após carregar a lista
    };

    this.institutionForm.patchValue(formValue);

    // Carrega as listas dependentes e, após carregar os cursos, ajusta o curso selecionado
    const ensinoID = formValue.instituicaoEnsinoID;
    const faseID = formValue.faseCursoID;
    if (ensinoID && faseID) {
      this.getAllInstituicaoCurso(
        {
          InstituicaoEnsinoID: ensinoID,
          CategoriaEnsinoCursoID: faseID,
          pageSize: 100,
        },
        (cursos: any[]) => {
          // Procura o curso cujo cursoID coincida com o do objeto editado
          const cursoID = bolsaInstituicaoCurso.cursoID;
          const encontrado = cursos.find((c) => c.cursoID === cursoID);
          if (encontrado) {
            // Usa o id da entidade (que é o campo que o dropdown espera como optionValue)
            this.institutionForm.patchValue({
              instituicaoEnsinoCursoID: encontrado.id,
            });
          } else {
            // Fallback: usa o valor original (pode não estar na lista, mas mantém)
            this.institutionForm.patchValue({
              instituicaoEnsinoCursoID:
                bolsaInstituicaoCurso.instituicaoEnsinoCursoID,
            });
          }
        },
      );
      this.loadAllUnidadeOrganica(ensinoID);
    }

    // Ajusta o campo fase conforme o tipo de bolsa
    if (this.bolsa) {
      this.handleFaseCursoByTipoBolsa(this.bolsa.tipoBolsa);
    }

    this.visible = true;
  }

  // ------------------------------------------------------------
  // SALVAR (CRIAR OU ATUALIZAR)
  // ------------------------------------------------------------
  addInstituicao(): void {
    if (this.institutionForm.invalid) {
      this.institutionForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Erro',
        detail: 'Por favor, preencha todos os campos obrigatórios.',
      });
      return;
    }

    const form = this.institutionForm.getRawValue(); // inclui campos desabilitados

    const basePayload = {
      bolsaID: this.bolsaId,
      instituicaoCursoID: form.instituicaoEnsinoCursoID,
      dataInicio: this.formatDateToISO(form.dataInicio),
      dataFim: this.formatDateToISO(form.dataFim),
      aproveitamento: form.aproveitamento ?? 1,
      anoCurricular: form.anoCurricular ?? 1,
    };

    let operation;
    if (this.currentEditIndex !== null) {
      // UPDATE
      const updatePayload = {
        ...basePayload,
        id: this.currentEditIndex,
        c: form.c || null, // campo de concorrência
      };
      operation = this.bolsaInstituicaoCursoService.update(updatePayload);
    } else {
      // CREATE
      operation = this.bolsaInstituicaoCursoService.store(basePayload);
    }

    operation.subscribe({
      next: (res: any) => {
        if (res.retorno.codigo === 200) {
          this.loadAllBolsaInstituicaoCurso();
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail:
              this.currentEditIndex !== null
                ? 'Fase de Formação actualizada com sucesso!'
                : 'Fase de Formação adicionada com sucesso!',
          });
          this.visible = false;
          this.institutionForm.reset();
          this.currentEditIndex = null;
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: res.retorno.mensagem,
          });
        }
      },
      error: (err) => {
        console.error('Erro na operação:', err);
        let detail =
          'Falha ao processar. Verifique os dados e tente novamente.';
        if (err?.error?.errors) {
          const msgs = Object.values(err.error.errors).flat().join('; ');
          detail = msgs || detail;
        }
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail,
        });
      },
    });
  }

  // ------------------------------------------------------------
  // ELIMINAR
  // ------------------------------------------------------------
  confirmDeleteBolsaInstituicaoCurso(bolsaInstituicaoCurso: any): void {
    this.confirmationService.confirm({
      message: 'Tem certeza que deseja eliminar esta fase de formação?',
      accept: () => this.deleteBolsaInstituicaoCurso(bolsaInstituicaoCurso?.id),
    });
  }

  deleteBolsaInstituicaoCurso(id: string): void {
    this.bolsaInstituicaoCursoService.delete(id).subscribe(
      (res: any) => {
        if (res.retorno.codigo === 200) {
          this.loadAllBolsaInstituicaoCurso();
          this.messageService.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: 'Fase de Formação eliminada com sucesso!',
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Erro',
            detail: res.retorno.mensagem,
          });
        }
      },
      (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erro',
          detail: 'Erro ao eliminar fase de formação!',
        });
      },
    );
  }

  // ------------------------------------------------------------
  // UTILITÁRIOS
  // ------------------------------------------------------------
  get bolsaId(): any {
    return this.route.snapshot.paramMap.get('id');
  }

  getSeverity(aproveitamento: string): any {
    const map: Record<string, string> = {
      ATIVO: 'success',
      INATIVO: 'secondary',
      SUSPENSO: 'danger',
      PENDENTE: 'warning',
      ENQUADRADO: 'info',
    };
    return map[aproveitamento.toUpperCase()] || 'info';
  }

  getStatus(status: string): string {
    const map: Record<string, string> = {
      Aprovado: 'Aprovado',
      em_curso: 'Em Curso',
      reprovado: 'Reprovado',
      suspensa: 'Suspensa',
      Pendente: 'Pendente',
    };
    return map[status] || 'Outro';
  }

  getStyle(status: string): string {
    const map: Record<string, string> = {
      Aprovado: 'success',
      em_curso: 'info',
      reprovado: 'danger',
      suspensa: 'warn',
      Pendente: 'warn',
    };
    return map[status] || 'warn';
  }

  onPageChange(event: any): void {
    this.pagination.pageNumber = event.page + 1;
    this.pagination.pageSize = event.rows;
    this.loadAllBolsaInstituicaoCurso();
  }

  onActionSelected(event: any): void {
    const { event: actionEvent, rowData } = event;
    this.actions
      .find((action) => action.label === actionEvent.item.label)
      ?.command?.({ item: rowData });
  }

  handleFormValueChange(event: any): void {
    this.loadAllBolsaInstituicaoCurso(event);
  }

  // ------------------------------------------------------------
  // EVENTOS DOS DROPDOWNS
  // ------------------------------------------------------------
  onInstitutionChange(event: any): void {
    this.updateDependentLists();
  }

  onFaseChange(event: any): void {
    this.updateDependentLists();
  }
}
